import AuditionHistoryList from '@src/components/aiVoices/AuditionHistoryList';
import { useAuditionHistory } from '@src/components/aiVoices/useAuditionHistory';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import VoiceAuditionPanel from '@src/components/aiVoices/VoiceAuditionPanel';
import VoiceInfoCard from '@src/components/aiVoices/VoiceInfoCard';
import Tabs from '@src/components/common/Tabs';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import ShowcaseAIVoicesPanel from './aiVoices/ShowcaseAIVoicesPanel';

/**
 * Settings → "AI Voices" tab: enable AI dubbing for the workspace and choose
 * the activated AI Voice pool (PLAT-10499), plus audition any voice — with a
 * custom phrase live (PLAT-10504) or by playing a pre-saved per-language sample
 * from the voice card (PLAT-10576). The section owns the selected voice so the
 * left audition panel and the right voice card stay in sync.
 */
const AIVoicesSection = observer(() => {
  const dubbingStore = useDubbingStore();
  const { catalog } = dubbingStore;
  // Shared immediate-persist activation (same path the top panel uses), so the
  // Add/Remove control and the combobox chips never diverge.
  const { organization, isActivated, toggleVoice, busy } = useVoiceActivation();
  // Workspace audition history (PLAT-10580) — lazily loaded when the History
  // tab opens, prepended when an audition here completes. State lives here (not
  // in the tab) so it survives tab switches and can receive the prepend.
  const history = useAuditionHistory(organization?.id);

  const [selectedVoiceId, setSelectedVoiceId] = useState(catalog[0]?.id ?? '');

  // Keep the selection valid as the catalog hydrates / changes underneath us.
  useEffect(() => {
    if (catalog.length > 0 && !catalog.some(v => v.id === selectedVoiceId)) {
      setSelectedVoiceId(catalog[0].id);
    }
  }, [catalog, selectedVoiceId]);

  const selectedVoice = catalog.find(v => v.id === selectedVoiceId) ?? null;

  return (
    <Section>
      <header>
        <h2>AI Voices</h2>
        <p>Enable AI dubbing and choose the AI voices this showcase can use.</p>
      </header>
      <ShowcaseAIVoicesPanel />

      <SubSection>
        <header>
          <h3>Audition a voice</h3>
          <p>
            Type a phrase and hear how a voice sounds saying it, or open a voice
            and play a sample in any language it supports.
          </p>
        </header>
        <AuditionLayout>
          <VoiceAuditionPanel
            voices={catalog}
            voiceId={selectedVoiceId}
            onVoiceChange={setSelectedVoiceId}
            activation={{
              isActivated: !!selectedVoice && isActivated(selectedVoice.id),
              onToggle: () => selectedVoice && toggleVoice(selectedVoice),
              busy,
              disabled: !organization,
            }}
            onAuditionComplete={history.prepend}
          />
          <Tabs
            onChange={tabId => {
              if (tabId === 'history') history.load();
            }}
            tabs={[
              {
                id: 'info',
                label: 'Voice Info',
                content: selectedVoice ? (
                  <CardCell>
                    <VoiceInfoCard voice={selectedVoice} />
                  </CardCell>
                ) : undefined,
              },
              {
                id: 'history',
                label: 'Audition History',
                content: (
                  <AuditionHistoryList
                    entries={history.entries}
                    loading={history.loading}
                    error={history.error}
                    hasMore={history.hasMore}
                    onShowMore={history.loadMore}
                  />
                ),
              },
            ]}
          />
        </AuditionLayout>
      </SubSection>
    </Section>
  );
});

export default AIVoicesSection;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  /* Compact this whole tab a step down (scoped token override) so more fits on
     screen — everything below inherits the smaller scale. */
  --text-md: 0.875rem; /* 16 → 14 */
  --text-base: 0.875rem; /* was inheriting ~16 → 14 */
  --text-sm: 0.8125rem; /* 14 → 13 */
  --text-xs: 0.6875rem; /* 12 → 11 */
  font-size: var(--text-sm);

  > header {
    display: flex;
    flex-direction: column;
    gap: 6px;

    > h2 {
      font-size: 17px;
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
      margin: 0;
    }

    > p {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      margin: 0;
    }
  }
`;

const AuditionLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  /* Top-align the columns; the card sets its own (taller) height so its
     language list + sample player both have room, rather than being clamped to
     the shorter left column. */
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Holds the voice card. Positioned relative with the card absolutely filling
// it; the fixed min-height gives the card room for the language list AND the
// sample player beneath it (so playing a sample doesn't squeeze the list). On
// the single-column (mobile) layout the card flows normally and grows.
const CardCell = styled.div`
  position: relative;
  min-height: 480px;

  @media (max-width: 900px) {
    position: static;
    min-height: 0;
  }
`;

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid var(--border);

  > header {
    display: flex;
    flex-direction: column;
    gap: 6px;

    > h3 {
      font-size: var(--text-base);
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
      margin: 0;
    }

    > p {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      margin: 0;
    }
  }
`;
